import { APP_URLS, CONSOLE_PATHS, consoleRoutes, PLAN_FEATURE_MIN_PLAN, SERVICE_NAV_REQUIREMENTS, type TenantNavEntry, type TenantNavLink } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  Breadcrumbs,
  Button,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Icon,
  MenuLink,
  NavItem,
  NavSectionLabel,
  SearchTrigger,
  Skeleton,
  ThemeToggle,
  Topbar,
  UserMenu,
  type Crumb,
} from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { OfferToast } from "../features/engage/inbox/offer-toast";
import { StatusPicker } from "../features/engage/inbox/status-picker";
import { usePermissions } from "../session/permissions";
import { useClient } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";
import { hrefFor, navLabel } from "./nav-model";
import { NotificationsMenu } from "./notifications-menu";
import { SidebarUsage } from "./sidebar-usage";

/** The topbar icon buttons grow to the 44 px a thumb needs on a touch screen (a mouse keeps the compact 36 px). */
const TOUCH_ICON = "pointer-coarse:h-11 pointer-coarse:w-11";

interface SidebarNavProps {
  entries: readonly TenantNavEntry[];
  isPending: boolean;
  activeId: string | undefined;
  agentId: string | null;
}

/** "Available on Standard": the plan a locked Service link needs, from the same table the server gates on. */
function lockedLabel(entry: TenantNavLink, t: (key: string, values?: Record<string, string>) => string): string | undefined {
  if (!entry.locked) return undefined;
  const requirement = (SERVICE_NAV_REQUIREMENTS as Record<string, { feature: keyof typeof PLAN_FEATURE_MIN_PLAN } | undefined>)[entry.id];
  const plan = requirement ? PLAN_FEATURE_MIN_PLAN[requirement.feature] : undefined;
  return t("service.lock.available", { plan: plan ? t(`console.plans.${plan}`) : "" });
}

export function ConsoleSidebarNav({ entries, isPending, activeId, agentId }: SidebarNavProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  if (isPending) {
    return (
      <div className="space-y-2 px-2 pt-4" aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-8 bg-chrome-3" />
        ))}
      </div>
    );
  }
  return entries.map((entry) => {
    if (entry.kind === "section") return <NavSectionLabel key={entry.id}>{t(entry.i18nKey)}</NavSectionLabel>;
    return (
      <NavItem
        key={entry.id}
        href={hrefFor(entry, summary.slug, agentId)}
        icon={entry.icon}
        label={navLabel(entry, (key) => t(key))}
        active={entry.id === activeId}
        locked={entry.locked === true}
        lockedLabel={lockedLabel(entry, t)}
      />
    );
  });
}

/** Role line under the user name: "Registrar · Owner". */
function useRoleLine(): string {
  const { t } = useTranslation();
  const { summary, me } = useWorkspaceContext();
  const roles = summary.roles.map((key) => t(`console.roles.${key}`, { defaultValue: key })).join(", ");
  return me.user.job_title ? t("console.roleLine", { title: me.user.job_title, role: roles }) : roles;
}

export function useSignOut() {
  const client = useClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await client.session.logout();
    queryClient.clear();
    await navigate(CONSOLE_PATHS.login, { replace: true });
  };
}

export function ConsoleSidebarFooter() {
  const { t } = useTranslation();
  const { summary, me } = useWorkspaceContext();
  const roleLine = useRoleLine();
  const signOut = useSignOut();
  return (
    <>
      <SidebarUsage />
      <UserMenu name={me.user.name} role={roleLine} triggerLabel={t("shell.userMenu")}>
        <MenuLink href={consoleRoutes.settings(summary.slug, "profile")} icon="user">
          {t("shell.myProfile")}
        </MenuLink>
        {me.user.is_platform_staff ? (
          <MenuLink href={APP_URLS.admin} icon="shield">
            {t("shell.platformAdmin")}
          </MenuLink>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem tone="danger" onSelect={() => void signOut()}>
          <Icon name="log-out" />
          {t("shell.logOut")}
        </DropdownMenuItem>
      </UserMenu>
    </>
  );
}

export function ConsoleTopbar({ crumbs, onOpenPalette }: { crumbs: readonly Crumb[]; onOpenPalette: () => void }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  return (
    <Topbar
      openMenuLabel={t("shell.openMenu")}
      breadcrumbs={<Breadcrumbs items={crumbs} label={t("shell.breadcrumb")} />}
      actions={
        <>
          <StatusPicker />
          <OfferToast />
          <SearchTrigger label={t("shell.search")} shortcut={t("shell.searchShortcut")} onOpen={onOpenPalette} />
          <ThemeToggle className={TOUCH_ICON} labels={{ trigger: t("theme.toggle"), heading: t("theme.heading"), light: t("theme.light"), dark: t("theme.dark"), system: t("theme.system") }} />
          {permissions.data ? <NotificationsMenu /> : null}
          <Button asChild variant="ghost" iconOnly aria-label={t("shell.help")} className={`hidden sm:inline-flex ${TOUCH_ICON}`}>
            <AppLink href={consoleRoutes.guide(summary.slug)}>
              <Icon name="help-circle" />
            </AppLink>
          </Button>
        </>
      }
    />
  );
}
